import React from "react";
import Bookshelf from "./Bookshelf";
import Panot from "./Panot";
import { ExternalPillLink, ShowcaseVisual } from "./ProjectShowcase";
import { trackAction } from "./PageAnalytics";
import usePointerTilt from "../../hooks/usePointerTilt";
import imgPortrait from "../../assets/images/imgpfp.webp";
import { built } from "../../data/homepage";
import { bookshelf } from "../../data/bookshelf";
import { testimonials } from "../../data/personal";

// The homepage after the work: the apps and plugins I built, then the person (a short
// introduction beside the portrait, with the shelf below), on its own ground
// that the footer continues. The footer, which is also the contact block, is
// Footer.js. Every section is a real anchor
// target: focusable, named, and clear of the sticky header when scrolled to.

function SectionHead({ id, title, children }) {
  return (
    <div className="pf-section__head">
      <h2 id={id} className="pf-section__title">
        {title}
      </h2>
      {children}
    </div>
  );
}

function SampleNote({ children }) {
  return <p className="pf-sample-note">{children}</p>;
}

// ---- Built ----
// The apps, then the Figma plugins. Every destination is on another site, so
// the media stays plain and the actions open in a new tab.

export function HomeBuilt() {
  return (
    <section
      id="built"
      tabIndex={-1}
      className="pf-section pf-shell"
      aria-labelledby="built-heading"
    >
      <SectionHead id="built-heading" title="Apps and tools I built" />
      <ul className="pf-built">
        {built.map((item) => (
          <li key={item.id} className="pf-built__item">
            <ShowcaseVisual project={item} className="pf-built__visual" linked={false} />
            <div className="pf-built__text">
              <h3 className="pf-built__title">
                {item.name}
                <span className="pf-built__kind">{item.kind}</span>
              </h3>
              <p className="pf-built__copy">{item.copy}</p>
              <div className="pf-built__actions">
                {item.links.map((link) => (
                  <ExternalPillLink
                    key={link.href}
                    href={link.href}
                    name={link.name}
                    onClick={() =>
                      trackAction(
                        "open_app_site",
                        item.links.length > 1 ? `${item.id}:${link.label}` : item.id
                      )
                    }
                  >
                    {link.label}
                  </ExternalPillLink>
                ))}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ---- About, then the shelf on the row below ----
// This is the only About: /about redirects here. The longer page it replaced
// is kept in docs/ABOUT_PAGE_ARCHIVE_2026-10-09.md.

// The portrait is a polaroid print with a shadow under it, at a slight
// angle; with a pointer it tips toward the cursor (usePointerTilt), and on
// touch it stays still.
function Portrait() {
  const tilt = usePointerTilt();
  return (
    <div className="pf-about__portrait" {...tilt.handlers}>
      <div className="pf-about__print" style={tilt.style}>
        <img
          src={imgPortrait}
          width="541"
          height="645"
          alt="Oscar Rabadán"
          loading="lazy"
          decoding="async"
        />
      </div>
    </div>
  );
}

export function HomeAbout() {
  return (
    <section
      id="about"
      tabIndex={-1}
      className="pf-section pf-section--about pf-band pf-shell"
      aria-labelledby="about-heading"
    >
      <Panot id="pf-panot-about" className="pf-band__pavement" />
      <div className="pf-about">
        <div className="pf-about__person">
          <div className="pf-about__intro">
            <SectionHead id="about-heading" title="About" />
            <p className="pf-about__lead">
              I’m Oscar, a product designer in Barcelona. The work I enjoy most turns a tangled
              problem into something people can use: understanding what is really going on for
              them, deciding what is worth building, and then caring for the interface down to its
              last state and word.
            </p>
            <p className="pf-about__text">
              I’m currently at PayRetailers, on platform and AI. Before that I worked with
              CaixaBank, CUPRA and ManoMano through Nateevo, and earlier on products for
              marketplaces, government and health: places where a wrong call costs someone money,
              time or trust.
            </p>
            <p className="pf-about__text">
              I learn by building things. Making my own tools and apps keeps my judgment close to
              the craft, because an idea only shows its real shape once it has to work. I also read
              about systems and deciding with incomplete information.
            </p>
          </div>
          <Portrait />
        </div>
        <Bookshelf shelf={bookshelf} />
      </div>
    </section>
  );
}

// ---- Testimonials (sample) ----
// Not on the homepage while the quotes are samples. One real quote with a
// meaningful attribution can go next to contact later.

export function HomeTestimonials() {
  const { items, sample } = testimonials;
  if (!items.length) return null;

  return (
    <section className="pf-section pf-shell" aria-labelledby="testimonials-heading">
      <SectionHead id="testimonials-heading" title="Working with me">
        {sample && <SampleNote>Sample quotes · fictional names, to be replaced</SampleNote>}
      </SectionHead>
      <ul className="pf-quotes">
        {items.map((item) => (
          <li key={item.name} className="pf-quote">
            <figure className="pf-quote__figure">
              <blockquote className="pf-quote__text">
                <p>“{item.quote}”</p>
              </blockquote>
              <figcaption className="pf-quote__by">
                {item.name}
                <span className="pf-quote__role">
                  {item.role}
                  {sample ? " · sample" : ""}
                </span>
              </figcaption>
            </figure>
          </li>
        ))}
      </ul>
    </section>
  );
}